import { useContext, useState } from 'react';
import { AuthContext } from '@urbanx/agx.auth.client';
import { SAVE_BUTTON_STATES } from '@urbanx/agx.ui-components2';
import { deleteFile } from 'services';
import { AgxToastState } from 'types/commonTypes';
import {
  StaffGetBlankContractDetailsForCampaignResponseBlankSalesContractStatus,
  StaffUpdateBlankContractDetailsForCampaignRequestBlankSalesContractStatus,
  UploadedDocument,
  useInvalidateUseStaffGetBlankContractDetailsForCampaign,
  useInvalidateUseStaffGetCampaign,
  useInvalidateUseStaffGetPropertyInformationProgressForCampaign,
  useStaffGetBlankContractDetailsForCampaign,
  useStaffUpdateBlankContractDetailsForCampaign,
  CampaignDetailModel,
} from '@urbanx/agx.campaigns.client';

interface UseContractSectionStateArgs {
  campaign?: CampaignDetailModel | undefined;
  updateToastState: (toastState: AgxToastState) => void;
}

interface ContractSectionState {
  contractDocument?: UploadedDocument | undefined;
  isPending: boolean;
  isError: boolean;
  pendingDelete: boolean;
  setPendingDelete: (value: boolean) => void;
  isFinalEnabled: boolean;
  isFinalSelected: boolean;
  setIsFinalSelected: (value: boolean) => void;
  hasUnsavedChanges: boolean;
  saveState: (typeof SAVE_BUTTON_STATES)[keyof typeof SAVE_BUTTON_STATES];
  handleSaveChanges: () => Promise<void>;
}

const useContractSectionState = ({
  campaign,
  updateToastState,
}: UseContractSectionStateArgs): ContractSectionState => {
  const { getAuthToken } = useContext(AuthContext);
  const [isSaving, setIsSaving] = useState(false);
  const [saveOutcome, setSaveOutcome] = useState<ContractSectionState['saveState']>();

  const {
    data: blankContractDetails,
    isPending,
    isError,
  } = useStaffGetBlankContractDetailsForCampaign(
    campaign?.campaignId ?? '',
    !!campaign?.campaignId
  );

  const invalidateStaffGetBlankContractDetails =
    useInvalidateUseStaffGetBlankContractDetailsForCampaign();
  const invalidateStaffGetPropertyInformationProgress =
    useInvalidateUseStaffGetPropertyInformationProgressForCampaign();
  const invalidateStaffGetCampaign = useInvalidateUseStaffGetCampaign();

  const { staffUpdateBlankContractDetailsForCampaign } =
    useStaffUpdateBlankContractDetailsForCampaign({
      onSuccess: () => undefined,
      onError: () => undefined,
    });

  const [finalChoice, setFinalChoice] = useState<boolean>();
  const [pendingDelete, setPendingDelete] = useState(false);

  // New server data (a save, a refetch, or another campaign) discards local choices.
  const [choicesBasis, setChoicesBasis] = useState(blankContractDetails);
  if (blankContractDetails !== choicesBasis) {
    setChoicesBasis(blankContractDetails);
    setFinalChoice(undefined);
    if (blankContractDetails?.blankSalesContract !== choicesBasis?.blankSalesContract)
      setPendingDelete(false);
  }

  const isFinalEnabled =
    blankContractDetails?.blankSalesContractStatus ===
    StaffGetBlankContractDetailsForCampaignResponseBlankSalesContractStatus.Final;
  const isFinalSelected = finalChoice ?? isFinalEnabled;
  const hasUnsavedChanges = pendingDelete || isFinalSelected !== isFinalEnabled;

  // A new edit clears the previous save's outcome.
  const [hadUnsavedChanges, setHadUnsavedChanges] = useState(hasUnsavedChanges);
  if (hasUnsavedChanges !== hadUnsavedChanges) {
    setHadUnsavedChanges(hasUnsavedChanges);
    if (hasUnsavedChanges) setSaveOutcome(undefined);
  }

  const saveState = isSaving
    ? SAVE_BUTTON_STATES.SAVING
    : (saveOutcome ?? SAVE_BUTTON_STATES.UNSAVED);

  const saveChanges = async () => {
    const campaignId = campaign?.campaignId ?? '';
    const document = blankContractDetails?.blankSalesContract;
    const authToken = await getAuthToken();

    await Promise.all([
      pendingDelete && document?.containerFilePath && authToken
        ? deleteFile(authToken, document.containerFilePath)
        : undefined,
      staffUpdateBlankContractDetailsForCampaign({
        campaignId,
        body: {
          blankSalesContractStatus: isFinalSelected
            ? StaffUpdateBlankContractDetailsForCampaignRequestBlankSalesContractStatus.Final
            : StaffUpdateBlankContractDetailsForCampaignRequestBlankSalesContractStatus.Draft,
          blankSalesContract: pendingDelete ? undefined : document,
        },
      }),
    ]);
    await Promise.all([
      invalidateStaffGetBlankContractDetails(campaignId),
      invalidateStaffGetPropertyInformationProgress(campaignId),
      // The save can change blankContractIsLive, which the campaign carries.
      invalidateStaffGetCampaign(campaignId),
    ]);
  };

  const handleSaveChanges = async () => {
    if (!hasUnsavedChanges || isSaving) return;
    // React Compiler cannot lower conditionals or `finally` inside try, so the try only awaits.
    const successMessage = pendingDelete
      ? 'Contract deleted successfully'
      : 'Contract saved successfully';
    setIsSaving(true);
    setSaveOutcome(undefined);

    try {
      await saveChanges();
      setSaveOutcome(SAVE_BUTTON_STATES.SAVED);
      setPendingDelete(false);
      updateToastState({ color: 'success', message: successMessage, open: true });
    } catch {
      setSaveOutcome(SAVE_BUTTON_STATES.ERROR);
      updateToastState({ color: 'error', message: 'Error saving contract', open: true });
    }
    setIsSaving(false);
  };

  return {
    contractDocument: blankContractDetails?.blankSalesContract,
    isPending,
    isError,
    pendingDelete,
    setPendingDelete,
    isFinalEnabled,
    isFinalSelected,
    setIsFinalSelected: setFinalChoice,
    hasUnsavedChanges,
    saveState,
    handleSaveChanges,
  };
};

export default useContractSectionState;
